import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, Users, FileText, Loader2, Search, Calendar, ChevronDown, ChevronRight, Check, ChevronsUpDown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import DateRangePicker from '@/components/portal/DateRangePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyCommissions({ userInfo }) {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [selectedPaidStatuses, setSelectedPaidStatuses] = useState([]);
  const [paidStatusOpen, setPaidStatusOpen] = useState(false);
  const [selectedPolicyOwners, setSelectedPolicyOwners] = useState([]);
  const [policyOwnerOpen, setPolicyOwnerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Bulk selection
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchMyCommissions();
  }, []);

  const fetchMyCommissions = async () => {
    try {
      setLoading(true);
      
      if (!userInfo?.agent_id) {
        toast.error('Agent ID not found');
        setLoading(false);
        return;
      }

      // Fetch user's own commissions
      const response = await axios.get(`${API}/portal/my-commissions`, {
        params: { agent_id: userInfo.agent_id }
      });

      const commissionsData = Array.isArray(response.data) ? response.data : [];
      
      // Debug: Log first commission to see available fields
      if (commissionsData.length > 0) {
        console.log('Sample commission data:', commissionsData[0]);
      }
      
      // Process data to keep both commission and policy data
      const processedData = commissionsData.map(comm => {
        const policyData = comm.policydata && comm.policydata.length > 0 ? comm.policydata[0] : {};
        return {
          // Commission data
          id: comm.id,
          created_at: comm.created_at,
          policy_id: comm.policy_id,
          amount: comm.commission_amount,
          paid_split_status: comm.paid_split,
          comment: comm.comment,
          agentonCommission_name: comm.agentonCommission_name || 'N/A',
          agentonCommission_npn: comm.agentonCommission_npn || 'N/A',
          submittedBy_name: comm.submittedBy_name || 'N/A',
          // Policy data (for quick access)
          policy_number: policyData.policy_number || 'N/A',
          client: policyData.client || 'N/A',
          agent_name: policyData.policyAgentowner_name || 'N/A',
          carrier_name: policyData.carrierName || 'N/A',
          carrier_product: policyData.carrier_product || 'N/A',
          // Store full policy data for expansion
          policyData: policyData
        };
      });
      
      setCommissions(processedData);
      
    } catch (err) {
      console.error('Error fetching my commissions:', err);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  // Get unique policy owners for filter
  const uniquePolicyOwners = useMemo(() => {
    const ownerSet = new Set();
    commissions.forEach(comm => {
      if (comm.agent_name && comm.agent_name !== 'N/A') {
        ownerSet.add(comm.agent_name);
      }
    });
    return Array.from(ownerSet).sort();
  }, [commissions]);

  // Filter commissions based on all filters
  const filteredCommissions = useMemo(() => {
    let filtered = commissions;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(comm => 
        (comm.agent_name && comm.agent_name.toLowerCase().includes(query)) ||
        (comm.client && comm.client.toLowerCase().includes(query)) ||
        (comm.policy_number && comm.policy_number.toLowerCase().includes(query)) ||
        (comm.carrier_name && comm.carrier_name.toLowerCase().includes(query)) ||
        (comm.agentonCommission_name && comm.agentonCommission_name.toLowerCase().includes(query)) ||
        (comm.amount && comm.amount.toString().includes(query))
      );
    }

    // Date range filter
    if (dateRange?.from) {
      const fromTimestamp = new Date(dateRange.from).getTime();
      const toTimestamp = dateRange.to ? new Date(dateRange.to).getTime() : Date.now();
      
      filtered = filtered.filter(comm => {
        const commissionDate = comm.created_at;
        return commissionDate >= fromTimestamp && commissionDate <= toTimestamp;
      });
    }

    // Paid split filter (multi-select)
    if (selectedPaidStatuses.length > 0) {
      filtered = filtered.filter(comm => {
        const status = comm.paid_split_status || 'N/A';
        return selectedPaidStatuses.some(selectedStatus => {
          if (selectedStatus === 'paid') {
            return status.toLowerCase() === 'paid';
          } else if (selectedStatus === 'unpaid') {
            return status.toLowerCase() === 'unpaid';
          } else if (selectedStatus === 'n/a') {
            return status === 'N/A' || !status;
          } else if (selectedStatus === 'chargeback') {
            return status.toLowerCase() === 'chargeback';
          }
          return false;
        });
      });
    }

    // Policy Owner filter
    if (selectedPolicyOwners.length > 0) {
      filtered = filtered.filter(comm => 
        selectedPolicyOwners.includes(comm.agent_name)
      );
    }

    return filtered;
  }, [commissions, searchQuery, dateRange, selectedPaidStatuses, selectedPolicyOwners]);

  // Pagination logic
  const paginatedCommissions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCommissions.slice(startIndex, endIndex);
  }, [filteredCommissions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCommissions.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedCommissions([]); // Clear selection when filters change
  }, [searchQuery, dateRange, selectedPaidStatuses, selectedPolicyOwners]);

  // Calculate summary metrics based on filtered data
  const summaryMetrics = useMemo(() => {
    const totalAmount = filteredCommissions.reduce((sum, comm) => {
      const amount = parseFloat(comm.amount) || 0;
      return sum + amount;
    }, 0);

    const uniqueAgents = new Set(filteredCommissions.map(comm => comm.agent_name)).size;
    const uniquePolicies = new Set(filteredCommissions.map(comm => comm.policy_id)).size;

    // Breakdown by paid status
    const paidCommissions = filteredCommissions.filter(comm => 
      comm.paid_split_status && 
      comm.paid_split_status.toLowerCase() === 'paid'
    );
    const paidAmount = paidCommissions.reduce((sum, comm) => sum + (parseFloat(comm.amount) || 0), 0);

    const naCommissions = filteredCommissions.filter(comm => 
      !comm.paid_split_status || 
      comm.paid_split_status === 'N/A'
    );
    const naAmount = naCommissions.reduce((sum, comm) => sum + (parseFloat(comm.amount) || 0), 0);

    const chargebackCommissions = filteredCommissions.filter(comm => 
      comm.paid_split_status && 
      comm.paid_split_status.toLowerCase() === 'chargeback'
    );
    const chargebackAmount = chargebackCommissions.reduce((sum, comm) => sum + (parseFloat(comm.amount) || 0), 0);

    const unpaidCommissions = filteredCommissions.filter(comm => 
      comm.paid_split_status && 
      comm.paid_split_status.toLowerCase() === 'unpaid'
    );
    const unpaidAmount = unpaidCommissions.reduce((sum, comm) => sum + (parseFloat(comm.amount) || 0), 0);

    return {
      totalRecords: filteredCommissions.length,
      totalAmount: totalAmount,
      uniqueAgents: uniqueAgents,
      uniquePolicies: uniquePolicies,
      // Per status breakdown
      paid: {
        count: paidCommissions.length,
        amount: paidAmount
      },
      na: {
        count: naCommissions.length,
        amount: naAmount
      },
      chargeback: {
        count: chargebackCommissions.length,
        amount: chargebackAmount
      },
      unpaid: {
        count: unpaidCommissions.length,
        amount: unpaidAmount
      }
    };
  }, [filteredCommissions]);

  // Handler for dashboard card clicks to filter by paid status
  const handleDashboardFilter = (filterType) => {
    switch(filterType) {
      case 'all':
        // Clear all filters
        setSearchQuery('');
        setSelectedPaidStatuses([]);
        setSelectedPolicyOwners([]);
        setDateRange(null);
        setCurrentPage(1);
        break;
      case 'paid':
        // Filter to paid commissions
        setSelectedPaidStatuses(['paid']);
        setCurrentPage(1);
        break;
      case 'unpaid':
        // Filter to unpaid commissions
        setSelectedPaidStatuses(['unpaid']);
        setCurrentPage(1);
        break;
      case 'chargeback':
        // Filter to chargeback
        setSelectedPaidStatuses(['chargeback']);
        setCurrentPage(1);
        break;
      default:
        break;
    }
  };

  const toggleRow = (commissionId) => {
    setExpandedRows(prev => ({
      ...prev,
      [commissionId]: !prev[commissionId]
    }));
  };

  // Bulk selection handlers
  const toggleSelectCommission = (commissionId) => {
    setSelectedCommissions(prev =>
      prev.includes(commissionId)
        ? prev.filter(id => id !== commissionId)
        : [...prev, commissionId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCommissions.length === paginatedCommissions.length) {
      setSelectedCommissions([]);
    } else {
      setSelectedCommissions(paginatedCommissions.map(c => c.id));
    }
  };

  const isAllSelected = paginatedCommissions.length > 0 && selectedCommissions.length === paginatedCommissions.length;
  const isSomeSelected = selectedCommissions.length > 0 && selectedCommissions.length < paginatedCommissions.length;

  // CSV Export function
  const exportToCSV = () => {
    setIsExporting(true);
    try {
      // Export all filtered commissions that are currently displayed
      const dataToExport = filteredCommissions;
      
      if (dataToExport.length === 0) {
        toast.error('No commissions to export');
        setIsExporting(false);
        return;
      }

      // CSV Headers
      const headers = [
        'Date Created',
        'Client',
        'Policy Number',
        'Commission Amount',
        'Paid Status',
        'Submitted By',
        'Comment',
        // Policy Data
        'Agent on Record',
        'Carrier',
        'Product',
        'Status',
        'Annual Premium',
        'Monthly Premium',
        'Face Amount',
        'Draft Date',
        'Phone',
        'State',
        'Source',
        'Type'
      ];

      // Build CSV rows
      const rows = dataToExport.map(comm => {
        const policy = comm.policyData || {};
        return [
          comm.created_at ? format(new Date(comm.created_at), 'yyyy-MM-dd HH:mm:ss') : '',
          comm.client || '',
          comm.policy_number || '',
          comm.amount || '',
          comm.paid_split_status || '',
          comm.submittedBy_name || '',
          comm.comment || '',
          // Policy data
          policy.policyAgentowner_name || '',
          policy.carrierName || '',
          policy.carrier_product || '',
          policy.policyStatus || '',
          policy.annual_premium || '',
          policy.monthly_premium || '',
          policy.face_amount || '',
          policy.initial_draft_date || '',
          policy.phone || '',
          policy.state || '',
          policy.sourceName || '',
          policy.typeName || ''
        ].map(field => `"${String(field).replace(/"/g, '""')}"`); // Escape quotes and wrap in quotes
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `my_commissions_export_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${dataToExport.length} commission records`);
      setSelectedCommissions([]); // Clear selection after export
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4af37] mx-auto mb-4" />
          <p className="text-neutral-600">Loading your commissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">My Commissions</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Your personal commission records and details
          </p>
        </div>
        <Badge className="bg-[#d4af37] hover:bg-[#b8941e] text-white">
          {commissions.length} Total
        </Badge>
      </div>

      {/* Summary Cards - Overall Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={() => handleDashboardFilter('all')} className="text-left">
          <Card className="border-[#d4af37]/20 cursor-pointer hover:shadow-lg hover:scale-105 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-600 mb-1">Total Records</p>
                  <p className="text-2xl font-bold text-neutral-800">{summaryMetrics.totalRecords}</p>
                  <p className="text-xs text-neutral-500 mt-1">Click to show all</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </button>

        <Card className="border-[#d4af37]/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Total Amount</p>
                <p className="text-2xl font-bold text-green-600">
                  ${summaryMetrics.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#d4af37]/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Unique Policies</p>
                <p className="text-2xl font-bold text-neutral-800">{summaryMetrics.uniquePolicies}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#d4af37]/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Unique Agents</p>
                <p className="text-2xl font-bold text-neutral-800">{summaryMetrics.uniqueAgents}</p>
              </div>
              <div className="w-12 h-12 bg-[#f4e5b8] rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-[#b8941e]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown by Paid Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={() => handleDashboardFilter('paid')} className="text-left">
          <Card className="border-green-200 bg-green-50/50 cursor-pointer hover:shadow-lg hover:scale-105 transition-all">
            <CardContent className="p-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-green-700">Paid</p>
                  <Badge className="bg-green-600 text-white">{summaryMetrics.paid.count}</Badge>
                </div>
                <p className="text-xl font-bold text-green-700">
                  ${summaryMetrics.paid.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </CardContent>
          </Card>
        </button>

        <button onClick={() => handleDashboardFilter('unpaid')} className="text-left">
          <Card className="border-orange-200 bg-orange-50/50 cursor-pointer hover:shadow-lg hover:scale-105 transition-all">
            <CardContent className="p-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-orange-700">Unpaid</p>
                  <Badge className="bg-orange-600 text-white">{summaryMetrics.unpaid.count}</Badge>
                </div>
                <p className="text-xl font-bold text-orange-700">
                  ${summaryMetrics.unpaid.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </CardContent>
          </Card>
        </button>

        <button onClick={() => handleDashboardFilter('chargeback')} className="text-left">
          <Card className="border-red-200 bg-red-50/50 cursor-pointer hover:shadow-lg hover:scale-105 transition-all">
            <CardContent className="p-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-red-700">Chargeback</p>
                  <Badge className="bg-red-600 text-white">{summaryMetrics.chargeback.count}</Badge>
                </div>
                <p className="text-xl font-bold text-red-700">
                  ${Math.abs(summaryMetrics.chargeback.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </CardContent>
          </Card>
        </button>

        <Card className="border-neutral-200 bg-neutral-50/50">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-neutral-700">N/A</p>
                <Badge className="bg-neutral-600 text-white">{summaryMetrics.na.count}</Badge>
              </div>
              <p className="text-xl font-bold text-neutral-700">
                ${summaryMetrics.na.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardContent className="p-6">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Search by client, agent, policy number, carrier, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Date Range Filter */}
            <div>
              <label className="text-xs text-neutral-600 mb-1 block">Date Range</label>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
              />
            </div>

            {/* Paid Split Filter - Multi-Select */}
            <div>
              <label className="text-xs text-neutral-600 mb-1 block">Paid Status</label>
              <Popover open={paidStatusOpen} onOpenChange={setPaidStatusOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={paidStatusOpen}
                    className="w-full justify-between"
                  >
                    {selectedPaidStatuses.length === 0
                      ? "All Statuses"
                      : `${selectedPaidStatuses.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search status..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                      {['paid', 'unpaid', 'chargeback', 'n/a'].map((status) => (
                        <div
                          key={status}
                          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          onClick={() => {
                            setSelectedPaidStatuses(prev =>
                              prev.includes(status)
                                ? prev.filter(s => s !== status)
                                : [...prev, status]
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedPaidStatuses.includes(status)}
                            className="mr-2"
                          />
                          {status === 'n/a' ? 'N/A' : status.charAt(0).toUpperCase() + status.slice(1)}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Agent on Record Filter */}
            <div>
              <label className="text-xs text-neutral-600 mb-1 block">Agent on Record</label>
              <Popover open={policyOwnerOpen} onOpenChange={setPolicyOwnerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={policyOwnerOpen}
                    className="w-full justify-between"
                  >
                    {selectedPolicyOwners.length === 0
                      ? "All Agents"
                      : `${selectedPolicyOwners.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agents..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                      {uniquePolicyOwners.map((owner) => (
                        <div
                          key={owner}
                          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          onClick={() => {
                            setSelectedPolicyOwners(prev =>
                              prev.includes(owner)
                                ? prev.filter(o => o !== owner)
                                : [...prev, owner]
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedPolicyOwners.includes(owner)}
                            className="mr-2"
                          />
                          {owner}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Reset Filters & Export Button */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {(dateRange || selectedPaidStatuses.length > 0 || selectedPolicyOwners.length > 0 || searchQuery) && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setDateRange(null);
                    setSelectedPaidStatuses([]);
                    setSelectedPolicyOwners([]);
                    setSearchQuery('');
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 w-auto px-4"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Clear All Filters
                </Button>
              )}
            </div>
            
            {/* Export Button */}
            {selectedCommissions.length > 0 && (
              <Button
                onClick={exportToCSV}
                disabled={isExporting}
                className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Export {selectedCommissions.length} Selected
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between text-sm text-neutral-600 mb-4">
            <div className="flex items-center gap-3">
              <span>
                Showing {filteredCommissions.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredCommissions.length)} of {filteredCommissions.length} records
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Rows per page:</span>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {totalPages > 1 && <span>Page {currentPage} of {totalPages}</span>}
          </div>

          {/* Commission Records Table */}
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto rounded-xl border border-neutral-100">
              {filteredCommissions.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-600">No commission records found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 w-12 first:rounded-tl-xl">
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isSomeSelected}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 w-8"></th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Date</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Client</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Policy Number</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Commission</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Paid Status</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 last:rounded-tr-xl">Submitted By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {paginatedCommissions.map((commission, index) => {
                      const amount = parseFloat(commission.amount) || 0;
                      const isNegative = amount < 0;
                      const isExpanded = expandedRows[commission.id];
                      const policy = commission.policyData || {};
                      
                      return (
                        <>
                          <tr key={commission.id || index} className="hover:bg-neutral-50 transition-all duration-200">
                            <td className="px-4 py-3">
                              <Checkbox
                                checked={selectedCommissions.includes(commission.id)}
                                onCheckedChange={() => toggleSelectCommission(commission.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => toggleRow(commission.id)}
                                className="p-1 hover:bg-neutral-200 rounded"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-[#d4af37]" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-neutral-400" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-600">
                              {commission.created_at ? format(new Date(commission.created_at), 'MMM dd, yyyy') : 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-800 font-medium">
                              {commission.client || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-600 font-mono">
                              {commission.policy_number || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold">
                              <span className={isNegative ? 'text-red-600' : 'text-green-600'}>
                                {isNegative ? '-' : ''}${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {commission.paid_split_status && commission.paid_split_status !== 'N/A' ? (
                                <Badge 
                                  variant="outline" 
                                  className={
                                    commission.paid_split_status.toLowerCase() === 'paid' 
                                      ? 'border-green-300 text-green-600'
                                      : commission.paid_split_status.toLowerCase() === 'chargeback'
                                      ? 'border-red-300 text-red-600'
                                      : 'border-orange-300 text-orange-600'
                                  }
                                >
                                  {commission.paid_split_status}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-neutral-300 text-neutral-600">
                                  N/A
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-600">
                              {commission.submittedBy_name || 'N/A'}
                            </td>
                          </tr>
                          
                          {/* Expanded Row - Policy Details */}
                          {isExpanded && (
                            <tr className="bg-neutral-50">
                              <td colSpan="8" className="px-4 py-4">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 mb-3">
                                    <FileText className="w-4 h-4 text-[#d4af37]" />
                                    <h4 className="font-semibold text-neutral-700 text-sm">Policy Details</h4>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <InfoField label="Agent on Record" value={policy.policyAgentowner_name || 'N/A'} />
                                    <InfoField label="Carrier" value={policy.carrierName || 'N/A'} />
                                    <InfoField label="Product" value={policy.carrier_product || 'N/A'} />
                                    <InfoField label="Status" value={policy.policyStatus || 'N/A'} />
                                    <InfoField label="Annual Premium" value={policy.annual_premium ? `$${policy.annual_premium}` : 'N/A'} />
                                    <InfoField label="Monthly Premium" value={policy.monthly_premium ? `$${policy.monthly_premium}` : 'N/A'} />
                                    <InfoField label="Face Amount" value={policy.face_amount ? `$${policy.face_amount.toLocaleString()}` : 'N/A'} />
                                    <InfoField label="Draft Date" value={policy.initial_draft_date || 'N/A'} />
                                    <InfoField label="State" value={policy.state || 'N/A'} />
                                    <InfoField label="Source" value={policy.sourceName || 'N/A'} />
                                    <InfoField label="Type" value={policy.typeName || 'N/A'} />
                                  </div>
                                  
                                  {commission.comment && commission.comment !== 'N/A' && (
                                    <div className="pt-3 border-t border-neutral-300">
                                      <p className="text-xs text-neutral-500 mb-1">Comment</p>
                                      <p className="text-sm text-neutral-700">{commission.comment}</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className={currentPage === pageNum ? "bg-[#d4af37] hover:bg-[#b8941e] text-white" : ""}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-neutral-500 mb-1">{label}</p>
      <p className="text-sm font-medium text-neutral-800">{value || 'N/A'}</p>
    </div>
  );
}
